import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../../components/modal-escape-guard/modal-escape-guard.directive';
import { Release } from '../../../models/release.model';
import { ReleaseService } from '../../../services/release.service';
import { fromIsoDate, toIsoDate } from '../../../shared/date.util';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { naechsteReleaseId } from './release-id-vorschlag';

type DialogFehler = 'keinRecht' | 'allgemein';

function pflichtOhneLeerzeichen(control: AbstractControl<string>): ValidationErrors | null {
  return control.value?.trim() ? null : { required: true };
}

/**
 * Dialog „Release anlegen“/„Release bearbeiten“ (NEX-2072, Rahmen wie `ReportModal` in
 * page-release-freigabe.jsx:100-199, Eintrag NEX-1972 18221). `release = null` legt an.
 *
 * Der Dialog liegt im `<form>` des Projektformulars: Alle Knöpfe sind `type="button"`,
 * die Felder stehen in einem `<div [formGroup]>`, und die Eingabetaste in einem Feld
 * speichert das Release, ohne das Projekt abzusenden.
 */
@Component({
  selector: 'app-release-dialog',
  imports: [
    Modal,
    ModalEscapeGuardDirective,
    TranslatePipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './release-dialog.component.html',
  styleUrl: './release-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReleaseDialogComponent {
  private readonly service = inject(ReleaseService);
  private readonly destroyRef = inject(DestroyRef);

  readonly visible = model(false);
  readonly projectId = input.required<string>();
  readonly release = input<Release | null>(null);
  readonly vorhandeneIds = input<readonly string[]>([]);
  readonly gespeichert = output<Release>();

  @ViewChild(Modal, { static: true }) private modal!: Modal;

  readonly idMax = FIELD_LENGTHS.KEY_MAX;
  readonly nameMax = FIELD_LENGTHS.NAME_MAX;

  readonly form = new FormGroup({
    id: new FormControl('', {
      nonNullable: true,
      validators: [pflichtOhneLeerzeichen, Validators.maxLength(this.idMax)],
    }),
    name: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(this.nameMax)] }),
    stichtag: new FormControl<Date | null>(null, { validators: [(c) => this.goLivePruefen(c)] }),
  });

  /** Zeigt die Go-Live-Meldungen schon beim Leeren, nicht erst beim Verlassen. */
  readonly sofortFehler: ErrorStateMatcher = {
    isErrorState: (control) => !!control?.invalid && (control.dirty || control.touched),
  };

  readonly istBearbeiten = computed(() => this.release() !== null);
  readonly speichert = signal(false);
  readonly dialogFehler = signal<DialogFehler | null>(null);

  private readonly formEreignis = toSignal(this.form.events);
  private readonly idWert = toSignal(this.form.controls.id.valueChanges, { initialValue: '' });

  readonly platzhalterId = computed(() => (this.istBearbeiten() ? '' : this.idWert().trim()));

  readonly speichernGesperrt = computed(() => {
    this.formEreignis();
    return this.speichert() || this.form.invalid || (this.istBearbeiten() && !this.form.dirty);
  });

  constructor() {
    effect(() => {
      if (this.visible()) untracked(() => this.zuruecksetzen());
    });
  }

  beiEingabetaste(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.speichern();
  }

  speichern(): void {
    if (this.speichernGesperrt()) return;
    const { id, name, stichtag } = this.form.getRawValue();
    const iso = stichtag ? toIsoDate(stichtag) : null;
    const release = this.release();
    const label = name.trim();
    const anfrage = release
      ? this.service.update(this.projectId(), release.id, { label: name, stichtag: iso })
      : this.service.create(this.projectId(), { id: id.trim(), ...(label ? { label } : {}), stichtag: iso });

    this.speichert.set(true);
    this.dialogFehler.set(null);
    anfrage.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (antwort) => {
        this.speichert.set(false);
        this.gespeichert.emit(antwort);
        this.visible.set(false);
      },
      error: (fehler: HttpErrorResponse) => {
        this.speichert.set(false);
        this.fehlerZuordnen(fehler.status);
      },
    });
  }

  abbrechen(): void {
    if (!this.speichert()) this.visible.set(false);
  }

  /**
   * `lib-modal` blendet sich beim Klick auf den Hintergrund selbst aus, bevor es meldet.
   * Während des Speicherns bleibt der Dialog offen, deshalb hier wieder einblenden.
   */
  beimSchliessenDurchModal(sichtbar: boolean): void {
    if (sichtbar) return;
    if (this.speichert()) {
      this.modal.visible = true;
      return;
    }
    this.visible.set(false);
  }

  /**
   * Go-Live ist nur beim Anlegen Pflicht. Ein gesetztes Go-Live lässt sich ändern, aber nicht leeren;
   * ein Bestands-Release ohne Go-Live bleibt ohne Datum speicherbar (NEX-2476).
   */
  private goLivePruefen(control: AbstractControl<Date | null>): ValidationErrors | null {
    if (control.value) return null;
    const release = this.release();
    if (!release) return { goLivePflicht: true };
    return release.stichtag ? { stichtagEntfernen: true } : null;
  }

  private zuruecksetzen(): void {
    const release = this.release();
    this.form.reset({
      id: release?.id ?? naechsteReleaseId(this.vorhandeneIds(), new Date()),
      name: release?.label ?? '',
      stichtag: release?.stichtag ? fromIsoDate(release.stichtag) : null,
    });
    this.dialogFehler.set(null);
    this.speichert.set(false);
  }

  private fehlerZuordnen(status: number): void {
    const { id, name } = this.form.controls;
    if (status === 409) {
      this.feldFehler(id, 'idVergeben');
    } else if (status === 400) {
      if (this.istBearbeiten()) this.feldFehler(name, 'nameUngueltig');
      else this.feldFehler(id, 'idUngueltig');
    } else {
      this.dialogFehler.set(status === 403 ? 'keinRecht' : 'allgemein');
    }
  }

  private feldFehler(control: AbstractControl, fehler: string): void {
    control.setErrors({ [fehler]: true });
    control.markAsTouched();
  }
}
